import { useState, useEffect } from 'react'

interface CurrencyInfo {
  code: string
  symbol: string
  rate: number
  countryName: string
  flag: string
}

// Approximate exchange rates for key LATAM + Spain markets (USD base)
// These are approximate and updated periodically
const CURRENCY_MAP: Record<string, CurrencyInfo> = {
  MX: {
    code: 'MXN',
    symbol: '$',
    rate: 17.2,
    countryName: 'México',
    flag: '🇲🇽',
  },
  AR: {
    code: 'ARS',
    symbol: '$',
    rate: 875,
    countryName: 'Argentina',
    flag: '🇦🇷',
  },
  CO: {
    code: 'COP',
    symbol: '$',
    rate: 3950,
    countryName: 'Colombia',
    flag: '🇨🇴',
  },
  CL: { code: 'CLP', symbol: '$', rate: 940, countryName: 'Chile', flag: '🇨🇱' },
  PE: {
    code: 'PEN',
    symbol: 'S/',
    rate: 3.72,
    countryName: 'Perú',
    flag: '🇵🇪',
  },
  UY: {
    code: 'UYU',
    symbol: '$',
    rate: 39.5,
    countryName: 'Uruguay',
    flag: '🇺🇾',
  },
  BR: {
    code: 'BRL',
    symbol: 'R$',
    rate: 4.97,
    countryName: 'Brasil',
    flag: '🇧🇷',
  },
  ES: {
    code: 'EUR',
    symbol: '€',
    rate: 0.92,
    countryName: 'España',
    flag: '🇪🇸',
  },
  EC: { code: 'USD', symbol: '$', rate: 1, countryName: 'Ecuador', flag: '🇪🇨' },
  PA: { code: 'USD', symbol: '$', rate: 1, countryName: 'Panamá', flag: '🇵🇦' },
  SV: {
    code: 'USD',
    symbol: '$',
    rate: 1,
    countryName: 'El Salvador',
    flag: '🇸🇻',
  },
  GT: {
    code: 'GTQ',
    symbol: 'Q',
    rate: 7.8,
    countryName: 'Guatemala',
    flag: '🇬🇹',
  },
  CR: {
    code: 'CRC',
    symbol: '₡',
    rate: 520,
    countryName: 'Costa Rica',
    flag: '🇨🇷',
  },
  DO: {
    code: 'DOP',
    symbol: 'RD$',
    rate: 57,
    countryName: 'Rep. Dominicana',
    flag: '🇩🇴',
  },
  HN: {
    code: 'HNL',
    symbol: 'L',
    rate: 24.7,
    countryName: 'Honduras',
    flag: '🇭🇳',
  },
  NI: {
    code: 'NIO',
    symbol: 'C$',
    rate: 36.6,
    countryName: 'Nicaragua',
    flag: '🇳🇮',
  },
  BO: {
    code: 'BOB',
    symbol: 'Bs',
    rate: 6.91,
    countryName: 'Bolivia',
    flag: '🇧🇴',
  },
  PY: {
    code: 'PYG',
    symbol: '₲',
    rate: 7300,
    countryName: 'Paraguay',
    flag: '🇵🇾',
  },
  VE: {
    code: 'VES',
    symbol: 'Bs',
    rate: 36.5,
    countryName: 'Venezuela',
    flag: '🇻🇪',
  },
}

interface UseCurrencyResult {
  isLocal: boolean
  localSymbol: string
  localCode: string
  countryFlag: string
  countryName: string
  convert: (usdAmount: number) => string
  isLoading: boolean
}

export function useCurrencyConversion(): UseCurrencyResult {
  const [countryCode, setCountryCode] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [liveRate, setLiveRate] = useState<number | null>(null)

  useEffect(() => {
    // Check sessionStorage first to avoid repeated API calls
    const cached = sessionStorage.getItem('user_country')
    if (cached) {
      setCountryCode(cached)
      setIsLoading(false)
      return
    }

    const fetchCountry = async () => {
      try {
        // Use ipapi.co free tier (no key needed, 1000 req/day)
        const res = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(3000),
        })
        if (!res.ok) throw new Error('API error')
        const data = await res.json()
        const code = data.country_code || data.country
        if (code) {
          sessionStorage.setItem('user_country', code)
          setCountryCode(code)
        }
      } catch {
        // Fallback: try alternative free API
        try {
          const res2 = await fetch('https://ip2c.org/s', {
            signal: AbortSignal.timeout(2000),
          })
          const text = await res2.text()
          // Format: "1;CC;CCC;Country Name"
          const parts = text.split(';')
          if (parts[0] === '1' && parts[1]) {
            sessionStorage.setItem('user_country', parts[1])
            setCountryCode(parts[1])
          }
        } catch {
          // Silent fail - will show USD
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchCountry()
  }, [])

  // Try to fetch live exchange rate for detected currency
  useEffect(() => {
    if (!countryCode) return
    const info = CURRENCY_MAP[countryCode]
    if (!info || info.code === 'USD') return

    const fetchRate = async () => {
      try {
        const res = await fetch(`https://open.er-api.com/v6/latest/USD`, {
          signal: AbortSignal.timeout(3000),
        })
        if (!res.ok) return
        const data = await res.json()
        if (data.rates && data.rates[info.code]) {
          setLiveRate(data.rates[info.code])
        }
      } catch {
        // Use static rate as fallback
      }
    }

    fetchRate()
  }, [countryCode])

  const currencyInfo = countryCode ? CURRENCY_MAP[countryCode] : null
  const isLocal = !!currencyInfo && currencyInfo.code !== 'USD'
  const rate = liveRate || currencyInfo?.rate || 1

  const convert = (usdAmount: number): string => {
    if (!isLocal) return ''
    const converted = usdAmount * rate
    // Format based on magnitude
    if (converted >= 1000) {
      return converted.toLocaleString('es', { maximumFractionDigits: 0 })
    }
    return converted.toLocaleString('es', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  return {
    isLocal,
    localSymbol: currencyInfo?.symbol || '$',
    localCode: currencyInfo?.code || 'USD',
    countryFlag: currencyInfo?.flag || '',
    countryName: currencyInfo?.countryName || '',
    convert,
    isLoading,
  }
}
